"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";

export default function LoginPage() {
  const router = useRouter();
  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function requestCode(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await fetch("/api/auth/request-otp", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email }),
      });
      setStep("code");
      setMessage(
        "Dacă adresa este înregistrată, veți primi un cod de autentificare pe email. Introduceți-l mai jos.",
      );
    } catch {
      setError("A apărut o eroare. Încercați din nou.");
    } finally {
      setBusy(false);
    }
  }

  async function verifyCode(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await fetch("/api/auth/verify-otp", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email, code }),
      });
      const data = (await res.json()) as { ok: boolean; redirect?: string; error?: string };
      if (data.ok && data.redirect) {
        router.push(data.redirect);
        router.refresh();
      } else {
        const map: Record<string, string> = {
          invalid: "Cod incorect sau expirat.",
          expired: "Codul a expirat. Solicitați unul nou.",
          too_many_attempts: "Prea multe încercări. Solicitați un cod nou.",
          rate_limited: "Prea multe cereri. Reîncercați mai târziu.",
        };
        setError(map[data.error ?? "invalid"] ?? "Autentificare eșuată.");
      }
    } catch {
      setError("A apărut o eroare. Încercați din nou.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        <div className="mb-6 flex flex-col items-center text-center">
          <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-600 text-2xl text-white">
            📚
          </span>
          <h1 className="text-lg font-semibold text-slate-900">Manuale Școlare</h1>
          <p className="mt-1 text-sm text-slate-500">Autentificare fără parolă prin cod pe email</p>
        </div>

        {message ? (
          <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
            {message}
          </div>
        ) : null}
        {error ? (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        {step === "email" ? (
          <form onSubmit={requestCode} className="space-y-4">
            <div>
              <label htmlFor="email" className="mb-1 block text-sm font-medium text-slate-700">
                Adresă de email
              </label>
              <input
                id="email"
                type="email"
                required
                autoComplete="email"
                placeholder="nume@scovoicu.ro"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
              />
            </div>
            <button
              className="w-full rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-60"
              disabled={busy}
            >
              {busy ? "Se trimite…" : "Trimite codul"}
            </button>
          </form>
        ) : (
          <form onSubmit={verifyCode} className="space-y-4">
            <div>
              <label htmlFor="code" className="mb-1 block text-sm font-medium text-slate-700">
                Cod de autentificare
              </label>
              <input
                id="code"
                inputMode="numeric"
                pattern="\d*"
                required
                autoComplete="one-time-code"
                placeholder="cod numeric"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-center text-lg tracking-widest outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
              />
            </div>
            <button
              className="w-full rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-60"
              disabled={busy}
            >
              {busy ? "Se verifică…" : "Autentificare"}
            </button>
            <button
              type="button"
              className="w-full rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
              onClick={() => {
                setStep("email");
                setCode("");
                setError(null);
              }}
            >
              Înapoi
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
